import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { QuickViewId } from "@/lib/messengerStoryLabels";
import MessengerUserProfile from "@/components/MessengerUserProfile";
import MessengerActiveFilters, {
  buildActiveFilterChips,
} from "@/components/messenger/MessengerActiveFilters";
import MessengerFilterSidebar, {
  buildClassOptions,
  buildPersonaOptions,
} from "@/components/messenger/MessengerFilterSidebar";
import MessengerPageShell from "@/components/messenger/MessengerPageShell";
import MessengerQuickViews from "@/components/messenger/MessengerQuickViews";
import MessengerStoryList from "@/components/messenger/MessengerStoryList";

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function deriveQuickViewId(
  storyClass?: string,
  persona?: string,
  hasPurchase?: boolean,
  q?: string,
  month?: string,
): QuickViewId {
  if (q?.trim() || month) return "all";
  if (hasPurchase) return "kupili";
  if (persona === "P6") return "opiekunka";
  if (storyClass === "produkt_zakup") return "produkt";
  if (storyClass === "historia_identyfikacja") return "historia";
  if (!storyClass && !persona && !hasPurchase) return "all";
  return "all";
}

export default function HistorieMessenger() {
  const [q, setQ] = useState("");
  const [storyClass, setStoryClass] = useState<string | undefined>();
  const [persona, setPersona] = useState<string | undefined>();
  const [month, setMonth] = useState<string | undefined>();
  const [hasPurchase, setHasPurchase] = useState<boolean | undefined>();
  const [profileParticipantId, setProfileParticipantId] = useState<string | null>(null);

  const debouncedQ = useDebounced(q, 300);

  const { data: facetsData } = useQuery({
    queryKey: ["messenger-stories-facets"],
    queryFn: api.messengerStoriesFacets,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["messenger-stories", debouncedQ, storyClass, persona, month, hasPurchase],
    queryFn: () =>
      api.messengerStories({
        q: debouncedQ || undefined,
        class: storyClass,
        persona,
        month,
        has_purchase: hasPurchase,
        limit: 100,
      }),
  });

  const facets = facetsData?.payload;
  const rows = data?.items ?? [];
  const indexExists = data?.index_exists ?? facets?.index_exists ?? false;
  const totalAll = facets?.total ?? 0;

  const classMap = facets?.classes ?? {};
  const personaMap = facets?.personas ?? {};
  const monthMap = facets?.months ?? {};

  const classOptions = buildClassOptions(classMap);
  const personaOptions = buildPersonaOptions(personaMap);
  const monthOptions = Object.entries(monthMap).sort((a, b) => b[0].localeCompare(a[0]));

  const activeQuickView = deriveQuickViewId(storyClass, persona, hasPurchase, debouncedQ, month);

  const quickViewCounts: Partial<Record<QuickViewId, number>> = {
    all: totalAll,
    historia: classMap.historia_identyfikacja,
    produkt: classMap.produkt_zakup,
    opiekunka: personaMap.P6,
  };

  const clearAllFilters = () => {
    setQ("");
    setStoryClass(undefined);
    setPersona(undefined);
    setMonth(undefined);
    setHasPurchase(undefined);
  };

  const applyQuickView = (id: QuickViewId) => {
    clearAllFilters();
    if (id === "historia") setStoryClass("historia_identyfikacja");
    if (id === "produkt") setStoryClass("produkt_zakup");
    if (id === "opiekunka") setPersona("P6");
    if (id === "kupili") setHasPurchase(true);
  };

  const activeChips = buildActiveFilterChips(
    { q, storyClass, persona, month, hasPurchase },
    {
      clearQ: () => setQ(""),
      clearClass: () => setStoryClass(undefined),
      clearPersona: () => setPersona(undefined),
      clearMonth: () => setMonth(undefined),
      clearPurchase: () => setHasPurchase(undefined),
    },
  );

  return (
    <MessengerPageShell
      total={data?.total ?? totalAll}
      q={q}
      onQChange={setQ}
    >
      <MessengerQuickViews
        activeId={activeQuickView}
        counts={quickViewCounts}
        onSelect={applyQuickView}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <MessengerFilterSidebar
          classOptions={classOptions}
          personaOptions={personaOptions}
          monthOptions={monthOptions}
          storyClass={storyClass}
          persona={persona}
          month={month}
          hasPurchase={hasPurchase}
          totalAll={totalAll}
          onClassChange={setStoryClass}
          onPersonaChange={setPersona}
          onMonthChange={setMonth}
          onPurchaseChange={setHasPurchase}
        />

        <div className="min-w-0 flex-1">
          <MessengerActiveFilters chips={activeChips} onClearAll={clearAllFilters} />
          <MessengerStoryList
            stories={rows}
            isLoading={isLoading}
            indexExists={indexExists}
            onOpenProfile={setProfileParticipantId}
          />
        </div>
      </div>

      <MessengerUserProfile
        participantId={profileParticipantId}
        onClose={() => setProfileParticipantId(null)}
      />
    </MessengerPageShell>
  );
}
